import {CurrencyFormatter} from '@fantasmagames/core/src/utils/CurrencyFormatter';
import {SliderNumberBox} from '@fantasmagames/slot/src/display/Slider/SliderNumberBox';
import {GameUtils} from '@fantasmagames/slot/src/utils/GameUtils';

export interface SliderValueChangedCallback {
	(newValue: number): void;
}

export class Slider extends PIXI.Sprite {
	public get currentValue(): number {
		return this._currentValue;
	}

	public set sliderNumberBox(box: SliderNumberBox) {
		this._sliderBox = box;
	}

	public set onValueChangedCallback(value: SliderValueChangedCallback) {
		this._onValueChangedCallback = value;
	}

	public set currencyFormatter(value: CurrencyFormatter) {
		this._currencyFormatter = value;
	}

	public get currencyFormatter(): CurrencyFormatter {
		return this._currencyFormatter;
	}

	public set showAsCurrency(value: boolean) {
		this._showAsCurrency = value;
	}

	public get showAsCurrency(): boolean {
		return this._showAsCurrency;
	}

	protected _background: PIXI.Sprite;
	protected _foreground: PIXI.Sprite;
	protected _dragger: PIXI.Sprite;

	protected _mask: PIXI.Graphics;

	protected _slot: PIXI.Container;
	protected _maskParent: PIXI.Container;

	protected _values: number[];
	protected _defaultValueIndex: number = -1;
	protected _currentValue: number;
	protected _sliderWidth: number;
	protected _startX: number;
	protected _draggerHalfWidth: number;
	protected _sliderHalfWidth: number;

	protected _isMobile: boolean;
	protected _dragging: boolean = false;

	protected _currencyFormatter: CurrencyFormatter;

	protected _showAsCurrency: boolean = true;
	protected _sliderBox: SliderNumberBox;
	protected _touchEvent: PIXI.interaction.InteractionData;

	protected _slots: number[];

	protected _onValueChangedCallback: SliderValueChangedCallback;

	constructor(
		slot: PIXI.Container,
		backgroundSprite: PIXI.Sprite,
		foregroundSprite: PIXI.Sprite,
		draggerSprite: PIXI.Sprite,
		isMobile: boolean,
	) {
		super();

		this._slot = slot;
		this._background = backgroundSprite;
		this._dragger = draggerSprite;
		this._sliderWidth = backgroundSprite.width;
		this._foreground = foregroundSprite;
		this._isMobile = isMobile;
		this._startX = -this._sliderWidth / 2;
		this._draggerHalfWidth = draggerSprite.width / 2;
		this._sliderHalfWidth = foregroundSprite.width / 2;

		this.initialize(slot, backgroundSprite, foregroundSprite, draggerSprite, isMobile);
	}

	public updateValues(values: number[], defaultValueIndex: number = -1): void {
		this._values = values;
		const length = values.length;
		this._slots = [];
		this._defaultValueIndex = defaultValueIndex;

		for (let i = 0; i < length; i++) {
			this._slots.push(Math.floor((i / this._values.length) * 100));
		}
	}

	public reset(): void {
		this._dragger.position.x = -this._sliderWidth / 2 + this._dragger.width / 2;
		if (this._slots && this._slots.length > 0) {
			if (this._defaultValueIndex >= 0) {
				this.updateValue(this._values[this._defaultValueIndex]);

				const percentage = this._slots[this._values.indexOf(this.currentValue)] / 100;
				this._dragger.position.x = this.calcSliderPosition(percentage);
			} else {
				this.updateValue(this._values[0]);
			}
		} else {
			this.updateValue(0);
		}
	}

	protected initialize(
		slot: PIXI.Container,
		backgroundSprite: PIXI.Sprite,
		foregroundSprite: PIXI.Sprite,
		draggerSprite: PIXI.Sprite,
		isMobile: boolean,
	): void {
		this.createSlider(slot, backgroundSprite, foregroundSprite);
		this.createDragger(slot, draggerSprite, backgroundSprite.width);
		this.createMask(this._dragger, this._maskParent, backgroundSprite.width, backgroundSprite.height);
		this.setUpInputs(isMobile);
	}

	protected createSlider(slot: PIXI.Container, backgroundSprite: PIXI.Sprite, foregroundSprite: PIXI.Sprite): void {
		slot.addChild(backgroundSprite);
		backgroundSprite.transform.updateTransform(slot.transform);
		backgroundSprite.scale.y *= -1; // Slots transform is flipped on y.
		backgroundSprite.anchor.set(0.5, 0.5);

		this._maskParent = new PIXI.Container();
		slot.addChild(this._maskParent);

		this._maskParent.addChild(foregroundSprite);
		foregroundSprite.transform.updateTransform(slot.transform);
		foregroundSprite.scale.y *= -1;
		foregroundSprite.anchor.set(0.5, 0.5);
	}

	protected createDragger(slot: PIXI.Container, sprite: PIXI.Sprite, width: number): void {
		sprite.anchor.set(0.5, 0.5);
		slot.addChild(sprite);
		sprite.interactive = true;
		sprite.buttonMode = true;
		sprite.scale.y *= -1;
		sprite.x = -width / 2 + sprite.width / 2;
	}

	protected createMask(slot: PIXI.Container, maskSlot: PIXI.Container, width: number, height: number): void {
		const mask = new PIXI.Graphics();
		mask.beginFill(0xffffff);
		mask.drawRect(slot.width / 3 - width, 0, width, height);
		mask.endFill();
		mask.y = -slot.height / 2;
		slot.addChild(mask);
		maskSlot.mask = mask;
	}

	protected setUpInputs(isMobile: boolean): void {
		this._dragger.on('touchstart', this.onDragStart.bind(this));
		this._dragger.on('touchmove', this.onDragMove.bind(this));
		this._dragger.on('touchend', this.onDragEnd.bind(this));
		this._dragger.on('touchendoutside', this.onDragEnd.bind(this));
		this._dragger.on('mousedown', this.onDragStart.bind(this));
		this._dragger.on('mousemove', this.onDragMove.bind(this));
		this._dragger.on('mouseup', this.onDragEnd.bind(this));
		this._dragger.on('mouseupoutside', this.onDragEnd.bind(this));

		this._touchEvent = null;
	}

	protected onDragStart(event: PIXI.interaction.InteractionEvent): void {
		this._touchEvent = event.data;
		this._dragging = true;
	}

	protected onDragMove(): void {
		if (this._dragging) {
			if (!this._touchEvent) {
				return;
			}
			const newPosition = this._touchEvent.getLocalPosition(this._dragger.parent);

			if (newPosition.x - this._draggerHalfWidth < this._startX) {
				this._dragger.position.x = this._startX + this._draggerHalfWidth;
				this.updateValue(this._values[0]);
			} else if (newPosition.x + this._draggerHalfWidth > this._sliderHalfWidth) {
				this._dragger.position.x = this._sliderHalfWidth - this._draggerHalfWidth;
				this.updateValue(this._values[this._values.length - 1]);
			} else {
				this._dragger.position.x = newPosition.x;
				for (let i = 0; i < this._slots.length; i++) {
					if (this.calcProcentage(newPosition.x) > this._slots[i]) {
						this.updateValue(this._values[i]);
					}
				}
			}
		}
	}

	protected calcProcentage(xPos: number): number {
		let procentage = xPos + this._sliderHalfWidth - this._draggerHalfWidth;
		procentage = Math.floor((procentage / (this._sliderWidth - this._dragger.width)) * 100);

		return procentage;
	}

	protected updateValue(value: number): void {
		this._currentValue = value;
		if (this._sliderBox) {
			const sliderBoxValue = this._showAsCurrency ? this.currencyFormatter.formatCurrency(value, true) : value.toString();
			this._sliderBox.updateNumber(sliderBoxValue);
		}

		if (this._onValueChangedCallback) {
			this._onValueChangedCallback.call(this, value);
		}
	}

	protected calcSliderPosition(percentage: number): number {
		const max = this._sliderHalfWidth - this._draggerHalfWidth;
		const min = this._startX + this._draggerHalfWidth;

		const position = GameUtils.lerp(min, max, percentage);

		return position;
	}

	protected onDragEnd(): void {
		this._touchEvent = null;
		this._dragging = false;
	}
}



// WEBPACK FOOTER //
// ./@fantasmagames/slot/src/display/Slider/Slider.ts